import React from 'react';
import { Link } from 'react-router-dom';
import { COMPANY_INFO, CORE_SERVICES, MAIN_AREAS } from '../data/siteData';

export default function Footer() {
  return (
    <footer className="bg-[#0f241a] text-[#d6e3db] border-t border-[#1b3d2c] pt-14 sm:pt-16 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Main Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-8 pb-12 border-b border-[#1f4734]">
          {/* Col 1: Brand & Contact Info */}
          <div className="lg:col-span-2 space-y-4">
            <Link
              to="/"
              className="inline-block bg-white p-0 rounded-2xl shadow-sm hover:shadow-md transition-all group overflow-hidden"
              aria-label="Hærvejens Træpleje forside"
            >
              <img
                src={COMPANY_INFO.logoUrl}
                alt="Hærvejens Træpleje"
                className="h-20 sm:h-24 md:h-28 max-h-[80px] w-auto object-contain transition-transform duration-300 group-hover:scale-[1.02]"
              />
            </Link>
            <p className="text-sm text-[#b8ccc0] max-w-sm leading-relaxed">
              Professionel træpleje for private og erhverv i hele Østjylland og Trekantområdet. Certificerede og uddannede arborister fra Skovskolen med fokus på sikkerhed, faglighed og pæn oprydning.
            </p>

            <div className="pt-2 space-y-2.5 text-sm">
              <p className="flex items-start gap-3">
                <i className="fa-solid fa-location-dot text-[#52b788] mt-1 shrink-0" aria-hidden="true" />
                <span>
                  {COMPANY_INFO.name}
                  <br />
                  {COMPANY_INFO.address}
                  <br />
                  {COMPANY_INFO.postalCode} {COMPANY_INFO.city}
                </span>
              </p>

              <p className="flex items-center gap-3">
                <i className="fa-solid fa-phone text-[#52b788] shrink-0" aria-hidden="true" />
                <a
                  href={`tel:${COMPANY_INFO.phoneRaw}`}
                  className="font-semibold text-white hover:text-[#52b788] transition-colors whitespace-nowrap"
                >
                  {COMPANY_INFO.phone}
                </a>
              </p>

              <p className="flex items-center gap-3">
                <i className="fa-solid fa-envelope text-[#52b788] shrink-0" aria-hidden="true" />
                <a
                  href={`mailto:${COMPANY_INFO.email}`}
                  className="hover:text-white transition-colors whitespace-nowrap"
                >
                  {COMPANY_INFO.email}
                </a>
              </p>

              <p className="flex items-center gap-3 text-xs text-[#9bb3a4]">
                <i className="fa-solid fa-id-card text-[#52b788] shrink-0" aria-hidden="true" />
                <span>CVR: {COMPANY_INFO.cvr}</span>
              </p>

              <div className="pt-1">
                <a
                  href={COMPANY_INFO.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#183827] text-[#a4d7bc] hover:text-white hover:bg-[#204a34] transition-colors"
                >
                  <i className="fa-solid fa-diamond-turn-right text-[#52b788]" aria-hidden="true" />
                  <span>Find vej på Google Maps</span>
                </a>
              </div>
            </div>
          </div>

          {/* Col 2: Services */}
          <div>
            <h4 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
              <i className="fa-solid fa-tree text-[#52b788] text-xs" aria-hidden="true" />
              <span>Ydelser</span>
            </h4>
            <ul className="space-y-2 text-sm text-[#b8ccc0]">
              {CORE_SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/${s.slug}`}
                    className="hover:text-white hover:underline underline-offset-4 transition-colors"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
              <li className="pt-1">
                <Link to="/services" className="text-xs font-semibold text-[#52b788] hover:underline">
                  Se alle ydelser »
                </Link>
              </li>
            </ul>
          </div>

          {/* Col 3: Areas */}
          <div>
            <h4 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
              <i className="fa-solid fa-map-location-dot text-[#52b788] text-xs" aria-hidden="true" />
              <span>Områder vi dækker</span>
            </h4>
            <ul className="space-y-2 text-sm text-[#b8ccc0]">
              {MAIN_AREAS.map((a) => (
                <li key={a.slug}>
                  <Link
                    to={`/${a.slug}`}
                    className="hover:text-white hover:underline underline-offset-4 transition-colors"
                  >
                    Træpleje i {a.name}
                  </Link>
                </li>
              ))}
              <li className="pt-1">
                <Link
                  to="/omraader"
                  className="text-xs font-semibold text-[#52b788] hover:underline"
                >
                  Områdeoversigt (Hub) »
                </Link>
              </li>
            </ul>
          </div>

          {/* Col 4: Trust & Navigation */}
          <div>
            <h4 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
              <i className="fa-solid fa-circle-info text-[#52b788] text-xs" aria-hidden="true" />
              <span>Hurtige links</span>
            </h4>
            <ul className="space-y-2 text-sm text-[#b8ccc0] mb-6">
              <li>
                <Link to="/om-os" className="hover:text-white transition-colors">
                  Om os & Teamet
                </Link>
              </li>
              <li>
                <Link to="/referencer" className="hover:text-white transition-colors">
                  Referencer & Galleri
                </Link>
              </li>
              <li>
                <Link to="/kontakt" className="hover:text-white transition-colors">
                  Kontakt os
                </Link>
              </li>
              <li>
                <Link to="/sitemap" className="hover:text-white transition-colors">
                  Sitemap
                </Link>
              </li>
            </ul>

            <h5 className="text-xs font-bold text-white mb-2">Sprog / Languages</h5>
            <div className="flex items-center gap-3 text-xs text-[#b8ccc0]">
              <Link to="/english" className="hover:text-white flex items-center gap-1.5">
                <i className="fa-solid fa-globe text-xs text-[#52b788]" aria-hidden="true" />
                <span>English</span>
              </Link>
              <span>•</span>
              <Link to="/deutsch" className="hover:text-white flex items-center gap-1.5">
                <i className="fa-solid fa-globe text-xs text-[#52b788]" aria-hidden="true" />
                <span>Deutsch</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Certifications & Trust Badges */}
        <div className="py-8 grid grid-cols-2 md:grid-cols-4 gap-4 text-xs text-[#9bb3a4] border-b border-[#1f4734]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#183827] text-[#52b788] flex items-center justify-center text-base shrink-0">
              <i className="fa-solid fa-graduation-cap" aria-hidden="true" />
            </div>
            <div>
              <p className="font-semibold text-white">Skovskolen</p>
              <p className="text-[11px]">Københavns Universitet</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#183827] text-[#52b788] flex items-center justify-center text-base shrink-0">
              <i className="fa-solid fa-shield-halved" aria-hidden="true" />
            </div>
            <div>
              <p className="font-semibold text-white">Topdanmark</p>
              <p className="text-[11px]">Forsikret op til 10 mio. kr.</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#183827] text-[#52b788] flex items-center justify-center text-base shrink-0">
              <i className="fa-solid fa-helmet-safety" aria-hidden="true" />
            </div>
            <div>
              <p className="font-semibold text-white">Arbejdstilsynet</p>
              <p className="text-[11px]">Strenge klatrestandarder</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#183827] text-[#52b788] flex items-center justify-center text-base shrink-0">
              <i className="fa-solid fa-handshake" aria-hidden="true" />
            </div>
            <div>
              <p className="font-semibold text-white">Hærvejsgruppen</p>
              <p className="text-[11px]">Landsdækkende partnerskab</p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-[#8ca394]">
          <p>
            © {new Date().getFullYear()} {COMPANY_INFO.name}. Alle rettigheder forbeholdes.
          </p>
          <div className="flex items-center gap-4 flex-wrap justify-center">
            <a
              href="https://www.iubenda.com/privacy-policy/76903169"
              className="iubenda-white iubenda-noiframe iubenda-embed"
              title="Privatlivspolitik "
            >
              Privatlivspolitik
            </a>
            <span className="text-[#357959]">•</span>
            <a
              href="https://www.iubenda.com/privacy-policy/76903169/cookie-policy"
              className="iubenda-white iubenda-noiframe iubenda-embed"
              title="Cookiepolitik "
            >
              Cookiepolitik
            </a>
          </div>
          <p className="flex items-center gap-2">
            <span>Professionelt arborist- og skovarbejde i Jylland og på Fyn</span>
          </p>
        </div>
      </div>
    </footer>
  );
}
